/**
 * jamcamConfig.ts
 *
 * Configuration settings for JamCam overlay display and processing.
 * Based on guitar-normal-guy config.js with full feature parity
 */

export interface JamCamConfig {
  display: {
    segmentations: {
      fret: {
        show: boolean;
        border: {
          show: boolean;
          color: string;
          thickness: number;
          opacity: number;
          style: 'solid' | 'dashed' | 'dotted';
        };
        fill: {
          show: boolean;
          color: string;
          opacity: number;
        };
        centroid: {
          show: boolean;
          diameter: number;
          color: string;
          opacity: number;
          border: {
            show: boolean;
            color: string;
            thickness: number;
            opacity: number;
            style: 'solid' | 'dashed' | 'dotted';
          };
        };
        label: {
          show: boolean;
          offsetX: number;
          offsetY: number;
          font: {
            style: string;
            size: number;
            color: string;
            opacity: number;
          };
          calloutLine: {
            show: boolean;
            connectionPoint: string;
            color: string;
            thickness: number;
            opacity: number;
            style: 'solid' | 'dashed' | 'dotted';
          };
        };
        douglasPeucker: {
          show: boolean;
          color: string;
          thickness: number;
          opacity: number;
          style: 'solid' | 'dashed' | 'dotted';
        };
        convexHull: {
          show: boolean;
          border: {
            show: boolean;
            color: string;
            thickness: number;
            opacity: number;
            style: 'solid' | 'dashed' | 'dotted';
          };
          fill: {
            show: boolean;
            color: string;
            opacity: number;
          };
          points: {
            show: boolean;
            diameter: number;
            color: string;
            opacity: number;
          };
          linearRegression: {
            show: boolean;
            color: string;
            thickness: number;
            opacity: number;
            style: 'solid' | 'dashed' | 'dotted';
          };
        };
        minAreaBoundingBox: {
          show: boolean;
          border: {
            show: boolean;
            color: string;
            thickness: number;
            opacity: number;
            style: 'solid' | 'dashed' | 'dotted';
          };
          fill: {
            show: boolean;
            color: string;
            opacity: number;
          };
        };
        linearRegression: {
          show: boolean;
          color: string;
          thickness: number;
          opacity: number;
          style: 'solid' | 'dashed' | 'dotted';
        };
        perpendicularEdgeLine: {
          show: boolean;
          color: string;
          thickness: number;
          opacity: number;
          style: 'solid' | 'dashed' | 'dotted';
          length: number;
          showGlobalRegression: boolean;
          globalColor: string;
          globalThickness: number;
          globalOpacity: number;
          globalStyle: 'solid' | 'dashed' | 'dotted';
        };
      };
      board: {
        show: boolean;
        border: {
          show: boolean;
          color: string;
          thickness: number;
          opacity: number;
          style: 'solid' | 'dashed' | 'dotted';
        };
        fill: {
          show: boolean;
          color: string;
          opacity: number;
        };
        centroid: {
          show: boolean;
          diameter: number;
          color: string;
          opacity: number;
        };
        label: {
          show: boolean;
          offsetX: number;
          offsetY: number;
          font: {
            style: string;
            size: number;
            color: string;
            opacity: number;
          };
        };
        convexHull: {
          show: boolean;
          border: {
            show: boolean;
            color: string;
            thickness: number;
            opacity: number;
            style: 'solid' | 'dashed' | 'dotted';
          };
          fill: {
            show: boolean;
            color: string;
            opacity: number;
          };
        };
        linearRegression: {
          show: boolean;
          color: string;
          thickness: number;
          opacity: number;
          style: 'solid' | 'dashed' | 'dotted';
        };
      };
    };
    fretboard: {
      topLine: {
        show: boolean;
        color: string;
        thickness: number;
        opacity: number;
        style: 'solid' | 'dashed' | 'dotted';
      };
      bottomLine: {
        show: boolean;
        color: string;
        thickness: number;
        opacity: number;
        style: 'solid' | 'dashed' | 'dotted';
      };
      strings: {
        show: boolean;
        count: number;
        color: string;
        thickness: number;
        opacity: number;
        style: 'solid' | 'dashed' | 'dotted';
      };
      perpendicularLines: {
        show: boolean;
        color: string;
        thickness: number;
        opacity: number;
        style: 'solid' | 'dashed' | 'dotted';
        length: number;
      };
      centroidsLinearRegression: {
        show: boolean;
        color: string;
        thickness: number;
        opacity: number;
        style: 'solid' | 'dashed' | 'dotted';
      };
    };
    notes: {
      show: boolean;
      showNames: boolean;
      ghost: {
        show: boolean;
        diameter: number;
        color: string;
        opacity: number;
        border: {
          show: boolean;
          color: string;
          thickness: number;
          opacity: number;
          style: 'solid' | 'dashed' | 'dotted';
        };
        names: {
          show: boolean;
          font: {
            style: string;
            size: number;
            color: string;
            opacity: number;
          };
        };
        useAlignedMethod: boolean;
        filledGhost: {
          show: boolean;
          diameter: number;
          color: string;
          opacity: number;
          border: {
            show: boolean;
            color: string;
            thickness: number;
            opacity: number;
            style: 'solid' | 'dashed' | 'dotted';
          };
        };
        gapFilling: {
          enabled: boolean;
          maxFretsToEstimate: number;
          estimatedFrets: {
            show: boolean;
            diameter: number;
            color: string;
            opacity: number;
            border: {
              show: boolean;
              color: string;
              thickness: number;
              opacity: number;
              style: 'solid' | 'dashed' | 'dotted';
            };
          };
          estimatedNotes: {
            show: boolean;
            diameter: number;
            color: string;
            opacity: number;
            border: {
              show: boolean;
              color: string;
              thickness: number;
              opacity: number;
              style: 'solid' | 'dashed' | 'dotted';
            };
          };
        };
      };
      scale: {
        show: boolean;
        diameter: number;
        color: string;
        opacity: number;
        border: {
          show: boolean;
          color: string;
          thickness: number;
          opacity: number;
          style: 'solid' | 'dashed' | 'dotted';
        };
        names: {
          show: boolean;
          font: {
            style: string;
            size: number;
            color: string;
            opacity: number;
          };
        };
      };
      fret: {
        show: boolean;
        diameter: number;
        color: string;
        opacity: number;
      };
    };
  };
  settings: {
    debugLogging: boolean;
    fretCount: number;
    tuning: string;
    laterality: 'left' | 'right';
    mirroring: boolean;
    enableProcessing: boolean;
    processing: {
      frequency: number; // Processing frequency in milliseconds
      keepLastOverlay: boolean; // Keep previous overlay until new one is ready
      smoothTransitions: boolean; // Enable smooth transitions between overlays
      transitionDuration: number; // Transition duration in milliseconds
    };
    scale: {
      type: string;
      key: string;
      mode: string;
      variation: string;
    };
    frets: {
      douglasPeuckerEpsilon: number;
      confidenceThreshold: number;
      maxNumberOfSegmentationsToShow: number;
      dedupe: boolean;
    };
    board: {
      douglasPeuckerEpsilon: number;
      confidenceThreshold: number;
    };
    strings: {
      count: number;
    };
  };
}

export const defaultJamCamConfig: JamCamConfig = {
  display: {
    segmentations: {
      fret: {
        show: true,
        border: {
          show: true,
          color: 'lime',
          thickness: 1,
          opacity: 0.5,
          style: 'dotted',
        },
        fill: {
          show: false,
          color: 'rgba(0,0,255,0.3)',
          opacity: 1.0,
        },
        centroid: {
          show: true,
          diameter: 4,
          color: 'rgba(0,0,255,0.5)',
          opacity: 1,
          border: {
            show: true,
            color: 'white',
            thickness: 1,
            opacity: 1.0,
            style: 'dashed',
          },
        },
        label: {
          show: true,
          offsetX: 5,
          offsetY: -10,
          font: {
            style: 'Arial',
            size: 12,
            color: 'blue',
            opacity: 1.0,
          },
          calloutLine: {
            show: true,
            connectionPoint: 'top',
            color: 'white',
            thickness: 1,
            opacity: 1.0,
            style: 'dashed',
          },
        },
        douglasPeucker: {
          show: false,
          color: 'yellow',
          thickness: 3,
          opacity: 0.1,
          style: 'dotted',
        },
        convexHull: {
          show: false,
          border: {
            show: true,
            color: 'pink',
            thickness: 2,
            opacity: 0.8,
            style: 'solid',
          },
          fill: {
            show: false,
            color: 'rgba(255, 192, 203, 0.3)',
            opacity: 0.3,
          },
          points: {
            show: false,
            diameter: 6,
            color: 'red',
            opacity: 0.9,
          },
          linearRegression: {
            show: false,
            color: 'purple',
            thickness: 2,
            opacity: 0.7,
            style: 'solid',
          },
        },
        minAreaBoundingBox: {
          show: false,
          border: {
            show: true,
            color: 'orange',
            thickness: 2,
            opacity: 0.8,
            style: 'solid',
          },
          fill: {
            show: false,
            color: 'rgba(255, 165, 0, 0.2)',
            opacity: 0.2,
          },
        },
        linearRegression: {
          show: false,
          color: 'cyan',
          thickness: 2,
          opacity: 0.7,
          style: 'solid',
        },
        perpendicularEdgeLine: {
          show: false,
          color: 'magenta',
          thickness: 1,
          opacity: 0.6,
          style: 'solid',
          length: 100,
          showGlobalRegression: false,
          globalColor: 'yellow',
          globalThickness: 3,
          globalOpacity: 0.8,
          globalStyle: 'dashed',
        },
      },
      board: {
        show: true,
        border: {
          show: true,
          color: 'red',
          thickness: 2,
          opacity: 0.7,
          style: 'solid',
        },
        fill: {
          show: false,
          color: 'rgba(255,0,0,0.2)',
          opacity: 0.2,
        },
        centroid: {
          show: false,
          diameter: 8,
          color: 'red',
          opacity: 1.0,
        },
        label: {
          show: false,
          offsetX: 10,
          offsetY: -15,
          font: {
            style: 'Arial',
            size: 14,
            color: 'red',
            opacity: 1.0,
          },
        },
        convexHull: {
          show: false,
          border: {
            show: true,
            color: 'darkred',
            thickness: 3,
            opacity: 0.8,
            style: 'solid',
          },
          fill: {
            show: false,
            color: 'rgba(139, 0, 0, 0.3)',
            opacity: 0.3,
          },
        },
        linearRegression: {
          show: false,
          color: 'maroon',
          thickness: 2,
          opacity: 0.7,
          style: 'solid',
        },
      },
    },
    fretboard: {
      topLine: {
        show: false,
        color: 'black',
        thickness: 2,
        opacity: 1.0,
        style: 'solid',
      },
      bottomLine: {
        show: false,
        color: 'black',
        thickness: 2,
        opacity: 1.0,
        style: 'solid',
      },
      strings: {
        show: false,
        count: 6,
        color: 'blue',
        thickness: 1,
        opacity: 0.1,
        style: 'solid',
      },
      perpendicularLines: {
        show: false,
        color: 'white',
        thickness: 1,
        opacity: 0.2,
        style: 'solid',
        length: 300,
      },
      centroidsLinearRegression: {
        show: false,
        color: 'red',
        thickness: 1,
        opacity: 0.5,
        style: 'solid',
      },
    },
    notes: {
      show: true,
      showNames: false,
      ghost: {
        show: true,
        diameter: 8,
        color: 'white',
        opacity: 1,
        border: {
          show: true,
          color: 'white',
          thickness: 1,
          opacity: 0.8,
          style: 'solid',
        },
        names: {
          show: false,
          font: {
            style: 'Arial',
            size: 10,
            color: 'white',
            opacity: 0.8,
          },
        },
        useAlignedMethod: true,
        filledGhost: {
          show: false,
          diameter: 6,
          color: 'rgba(255, 255, 255, 0.8)',
          opacity: 0.8,
          border: {
            show: true,
            color: 'rgba(255, 255, 255, 0.6)',
            thickness: 1,
            opacity: 0.6,
            style: 'solid',
          },
        },
        gapFilling: {
          enabled: false,
          maxFretsToEstimate: 5,
          estimatedFrets: {
            show: true,
            diameter: 6,
            color: 'rgba(255,255,0,0.6)',
            opacity: 0.6,
            border: {
              show: true,
              color: 'rgba(255,255,0,0.8)',
              thickness: 1,
              opacity: 0.8,
              style: 'dashed',
            },
          },
          estimatedNotes: {
            show: true,
            diameter: 6,
            color: 'rgba(255,255,0,0.7)',
            opacity: 0.7,
            border: {
              show: true,
              color: 'rgba(255,255,0,0.9)',
              thickness: 1,
              opacity: 0.9,
              style: 'dashed',
            },
          },
        },
      },
      scale: {
        show: false,
        diameter: 6,
        color: 'rgb(81, 255, 0)',
        opacity: 0.7,
        border: {
          show: true,
          color: 'rgb(255, 255, 255)',
          thickness: 1,
          opacity: 0.8,
          style: 'solid',
        },
        names: {
          show: false,
          font: {
            style: 'Arial',
            size: 8,
            color: 'rgb(255, 255, 255)',
            opacity: 0.5,
          },
        },
      },
      fret: {
        show: false, // Don't show both ghost and fret notes to avoid duplicates
        diameter: 8,
        color: 'white',
        opacity: 1,
      },
    },
  },
  settings: {
    debugLogging: false,
    fretCount: 20,
    tuning: 'standard',
    laterality: 'right',
    mirroring: false,
    enableProcessing: false,
    processing: {
      frequency: 1000, // Process every 1000ms (1 second) by default
      keepLastOverlay: true, // Keep previous overlay until new one is ready
      smoothTransitions: true, // Enable smooth transitions
      transitionDuration: 300, // 300ms transition duration
    },
    scale: {
      type: 'major',
      key: 'G',
      mode: 'ionian',
      variation: 'pentatonic',
    },
    frets: {
      douglasPeuckerEpsilon: 12,
      confidenceThreshold: 0.3,
      maxNumberOfSegmentationsToShow: 35,
      dedupe: false,
    },
    board: {
      douglasPeuckerEpsilon: 12,
      confidenceThreshold: 0.3,
    },
    strings: {
      count: 6,
    },
  },
};
